import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Cookie } from "lucide-react";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = "adsdrive_cookie_consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowBanner(false);
    setShowCustomize(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-fade-in">
        <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-2 bg-background">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  We Value Your Privacy
                </h3>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your browsing experience, provide
                  security features like reCAPTCHA, and analyse site traffic. By
                  clicking "Accept All", you consent to our use of cookies. You
                  can customize your preferences or accept only essential cookies.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                onClick={() => setShowCustomize(true)}
                className="w-full sm:w-auto"
              >
                Customize
              </Button>
              <Button
                variant="outline"
                onClick={acceptEssential}
                className="w-full sm:w-auto"
              >
                Essential Only
              </Button>
              <Button
                onClick={acceptAll}
                className="w-full sm:w-auto"
              >
                Accept All
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customize Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which types of cookies you want to allow. Essential cookies
              cannot be disabled as they are necessary for the site to function.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg bg-muted/30">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Label className="font-semibold text-foreground">
                    Essential Cookies
                  </Label>
                  <span className="text-xs px-2 py-1 bg-accent text-accent-foreground rounded">
                    Always Active
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  These cookies are necessary for the website to function and
                  cannot be disabled. They include security features, form
                  functionality, and basic site operations.
                </p>
              </div>
              <Switch checked={true} disabled className="mt-1" />
            </div>

            <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label
                  htmlFor="analytics"
                  className="font-semibold text-foreground block mb-2"
                >
                  Analytics Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies help us understand how visitors interact with our
                  website by collecting anonymous information. This helps us
                  improve user experience and site performance.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="mt-1"
              />
            </div>

            <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label
                  htmlFor="marketing"
                  className="font-semibold text-foreground block mb-2"
                >
                  Marketing Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies are used to track visitors across websites and
                  display relevant advertisements. We currently do not use
                  marketing cookies, but this option is available for future use.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCustomize(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={saveCustomPreferences} className="flex-1">
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
